// Minimal shadcn-style primitives, restyled to Lucy's redesign language
// (deep-navy glass surfaces, violet primary, gold/mint accents). Hand-rolled
// to keep zero dependency on the shadcn CLI workflow — copy/edit freely.
// Public props/variant APIs are unchanged so every screen keeps working.
import * as React from "react"
import { cn } from "@/lib/utils"

export function Button(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "default" | "secondary" | "ghost" | "destructive" | "outline"
    size?: "sm" | "md" | "lg" | "icon"
  },
) {
  const { className, variant = "default", size = "md", ...rest } = props
  return (
    <button
      {...rest}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-xl text-sm font-medium transition disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
        variant === "default" &&
          "bg-primary text-primary-foreground shadow-[0_4px_14px_rgba(243,183,87,0.35)] hover:brightness-110",
        variant === "secondary" &&
          "bg-secondary text-foreground ring-1 ring-inset ring-border hover:brightness-110",
        variant === "ghost" && "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground",
        variant === "destructive" &&
          "bg-destructive/90 text-destructive-foreground hover:bg-destructive",
        variant === "outline" &&
          "border border-border bg-foreground/[0.04] text-foreground hover:bg-foreground/[0.06] hover:border-border",
        size === "md" && "h-9 px-4",
        size === "sm" && "h-8 px-3 text-xs",
        size === "lg" && "h-10 px-5 text-base",
        size === "icon" && "h-9 w-9",
        className,
      )}
    />
  )
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { className, ...rest } = props
  return (
    <input
      {...rest}
      className={cn(
        "flex h-11 w-full rounded-xl border border-border bg-background px-3.5 py-1 text-sm transition placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    />
  )
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { className, ...rest } = props
  return (
    <textarea
      {...rest}
      className={cn(
        "flex w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm leading-relaxed transition placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    />
  )
}

export function Card({
  className, children, onClick,
}: { className?: string; children: React.ReactNode; onClick?: React.MouseEventHandler<HTMLDivElement> }) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "rounded-2xl bg-card text-card-foreground ring-1 ring-inset ring-border",
        className,
      )}
    >
      {children}
    </div>
  )
}

export function CardHeader({
  title, subtitle, right, accent,
}: { title: string; subtitle?: string; right?: React.ReactNode; accent?: string }) {
  return (
    <div className="flex items-start justify-between border-b border-border p-5">
      <div>
        <h2 className="flex items-center gap-2 text-[18px] font-medium tracking-tight">
          {accent && (
            <span
              className="h-[7px] w-[7px] rounded-full"
              style={{ background: accent, boxShadow: `0 0 10px ${accent}` }}
            />
          )}
          {title}
        </h2>
        {subtitle && (
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {right}
    </div>
  )
}

export function CardBody({
  className, children,
}: { className?: string; children: React.ReactNode }) {
  return <div className={cn("p-5", className)}>{children}</div>
}

export function Badge({
  variant = "default", className, children,
}: {
  variant?: "default" | "ok" | "warn" | "destructive" | "muted"
  className?: string
  children: React.ReactNode
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        variant === "default" && "border-primary/25 bg-primary/15 text-[hsl(var(--violet))]",
        variant === "ok" && "border-mint/25 bg-mint/15 text-mint",
        variant === "warn" && "border-gold/25 bg-gold/15 text-gold",
        variant === "destructive" && "border-destructive/30 bg-destructive/15 text-destructive",
        variant === "muted" && "border-border bg-foreground/[0.04] text-muted-foreground",
        className,
      )}
    >
      {children}
    </span>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent align-[-0.125em]",
        className,
      )}
    />
  )
}

/** Empty / loading / error placeholder. */
export function Stub({
  title, hint, children,
}: { title: string; hint?: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-foreground/[0.04] p-8 text-center text-sm text-muted-foreground">
      <div className="font-medium text-foreground">{title}</div>
      {hint && <div className="mt-1">{hint}</div>}
      {children && <div className="mt-3">{children}</div>}
    </div>
  )
}

/** Modal scaffold. Pass `open` and `onClose`. Renders portal-style. */
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

export function Sheet({
  open, onClose, title, children, footer,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  const box = React.useRef<HTMLDivElement>(null)
  const titleId = React.useId()
  // Focus trap: focus the first control on open, keep Tab inside, Esc
  // closes, and hand focus back to where it was.
  React.useEffect(() => {
    if (!open) return
    const prev = document.activeElement as HTMLElement | null
    const first = box.current?.querySelector<HTMLElement>(FOCUSABLE)
    requestAnimationFrame(() => (first ?? box.current)?.focus())
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return }
      if (e.key !== "Tab" || !box.current) return
      const nodes = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null)
      if (nodes.length === 0) { e.preventDefault(); return }
      const a = nodes[0], z = nodes[nodes.length - 1]
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus() }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus() }
    }
    document.addEventListener("keydown", onKey)
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.() }
  }, [open, onClose])
  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-md animate-modal-in overflow-hidden rounded-2xl border border-border bg-card shadow-[0_24px_60px_rgba(0,0,0,0.6)] outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border p-5">
          <h2 id={titleId} className="text-[15px] font-semibold tracking-tight">{title}</h2>
        </div>
        <div className="p-5">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-border p-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

/** Toast — inline, banner-style. Caller controls dismissal. */
export function Toast({
  variant = "default", children,
}: {
  variant?: "default" | "ok" | "warn" | "destructive"
  children: React.ReactNode
}) {
  return (
    <div className={cn(
      "rounded-xl border p-3 text-xs",
      variant === "ok" && "border-mint/30 bg-mint/10 text-mint",
      variant === "warn" && "border-gold/30 bg-gold/10 text-gold",
      variant === "destructive" && "border-destructive/30 bg-destructive/10 text-destructive",
      variant === "default" && "border-border bg-card text-foreground",
    )}>
      {children}
    </div>
  )
}
